import { easternDateKey, formatEasternDateHeader } from '@/lib/date';

import type { Notification } from './schemas';

/** React key for the degenerate group holding rows whose timestamps could not be read at all. */
const UNDATED_KEY = 'undated';

export type NotificationDateGroup = {
  /** ET calendar day, `YYYY-MM-DD` — the stable React key. */
  key: string;
  /** "Today", "Jan 15", or "Jan 15, 2025". */
  label: string;
  items: Notification[];
};

/**
 * Bucket notifications under ET date headers, preserving the server's newest-first order
 * (US-101849 § A: "rows are grouped under date headers / notifications are ordered newest-first").
 *
 * The order of the groups, and of rows inside them, is whatever order the server sent — this does
 * not re-sort. The server owns ordering, and re-sorting here would silently disagree with the
 * cursor paging: a later page's rows would need merging into an earlier group.
 *
 * Grouping is keyed on the ET day (ADR-0019), not the UTC day, so a 23:30 ET event stays under
 * that day's header instead of jumping to tomorrow.
 */
export function groupNotificationsByDate(items: Notification[]): NotificationDateGroup[] {
  const groups: NotificationDateGroup[] = [];
  // Undated rows seen before any header exists. They cannot open a group of their own, and
  // dropping them would hide real notifications, so they wait for the first group and lead it.
  const orphans: Notification[] = [];

  for (const item of items) {
    const key = easternDateKey(item.createdAt);
    // No parseable timestamp means no header to file it under, so the row joins the group being
    // built rather than starting a headerless one — or, if none has been built yet, it waits.
    const target = key ?? groups.at(-1)?.key;
    if (target == null) {
      orphans.push(item);
      continue;
    }

    const last = groups.at(-1);
    if (last?.key === target) {
      last.items.push(item);
      continue;
    }
    groups.push({
      key: target,
      label: formatEasternDateHeader(item.createdAt) ?? target,
      items: [...orphans.splice(0), item],
    });
  }

  // Only reachable when *every* row was unparseable, so there is genuinely no date to head them
  // with. A headerless group still renders them; silently returning nothing would not.
  if (orphans.length > 0) {
    groups.push({ key: UNDATED_KEY, label: '', items: orphans });
  }

  return groups;
}
